// =============================================================================
// gamesApi — best-effort server sync for the arcade.
//
// The arcade is localStorage-first (gamesLib is the synchronous source of truth
// the UI reads). These helpers push/pull to the per-user /api/games/* endpoints
// so scores are cross-device and the GLOBAL members leaderboard has data. EVERY
// function fails soft: no token / offline / server error → it no-ops (or returns
// []), so nothing here can break the offline experience.
// =============================================================================

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function authToken(): string | null {
  try { return localStorage.getItem('akt_auth_token'); } catch { return null; }
}

export function hasGamesApi(): boolean {
  return !!authToken();
}

function headers(): Record<string, string> {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken()}` };
}

async function post(path: string, body: unknown): Promise<any | null> {
  if (!authToken()) return null;
  try {
    const res = await fetch(`${API_BASE}/games${path}`, { method: 'POST', headers: headers(), body: JSON.stringify(body) });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

async function get(path: string): Promise<any | null> {
  if (!authToken()) return null;
  try {
    const res = await fetch(`${API_BASE}/games${path}`, { headers: headers() });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

// --- Scores ---
export function syncMyScore(gameCode: string, best: number, plays: number, level: number): void {
  // fire-and-forget
  void post('/score', { game_code: gameCode, best_score: best, plays, level });
}

export interface ServerScore { game_code: string; best_score: number; last_score: number; plays: number; level: number; }

/** The caller's own bests from PG (used to hydrate the local cache). */
export async function fetchScores(): Promise<ServerScore[]> {
  const rows = await get('/scores');
  return Array.isArray(rows) ? rows : [];
}

// Awaitable pushes used by the full reconcile (so we can order friend→peer-score
// and know when PG actually holds everything).
export async function pushScore(gameCode: string, best: number, plays: number, level: number): Promise<boolean> {
  return !!(await post('/score', { game_code: gameCode, best_score: best, plays, level }));
}
export async function pushFriend(localKey: string, name: string, kind: string): Promise<boolean> {
  return !!(await post('/friend', { local_key: localKey, name, kind }));
}
export async function pushPeerScore(localKey: string, gameCode: string, best: number): Promise<boolean> {
  return !!(await post('/peer-score', { local_key: localKey, game_code: gameCode, best_score: best }));
}

export interface GlobalLeaderRow { user_id: number; name: string; best: number; isYou: boolean; }

export async function fetchGlobalLeaderboard(gameCode: string): Promise<GlobalLeaderRow[]> {
  const rows = await get(`/leaderboard/${gameCode}`);
  return Array.isArray(rows) ? rows : [];
}

// --- Friends / peers (so the roster is portable across devices) ---
export function syncFriend(localKey: string, name: string, kind: string): void {
  void post('/friend', { local_key: localKey, name, kind });
}

export function syncFriendInvited(localKey: string): void {
  if (!authToken()) return;
  void fetch(`${API_BASE}/games/friend/${encodeURIComponent(localKey)}/invited`, { method: 'PUT', headers: headers() }).catch(() => {});
}

export function removeFriendRemote(localKey: string): void {
  if (!authToken()) return;
  void fetch(`${API_BASE}/games/friend/${encodeURIComponent(localKey)}`, { method: 'DELETE', headers: headers() }).catch(() => {});
}

export function syncPeerScore(localKey: string, gameCode: string, best: number): void {
  void post('/peer-score', { local_key: localKey, game_code: gameCode, best_score: best });
}

export interface ServerFriend {
  friend_id: number;
  local_key: string | null;
  name: string;
  kind: string;
  invited: number;
  scores: Record<string, number>;
}

export async function fetchFriends(): Promise<ServerFriend[]> {
  const rows = await get('/friends');
  return Array.isArray(rows) ? rows : [];
}
